import { useState, useEffect } from 'react';
import { CheckCircle2, BarChart3, TrendingUp, Target, Clock, Shield, Zap, FileText, PieChart, Calendar, Users, Award, ArrowRight, DollarSign, Activity, LineChart, Sprout } from 'lucide-react';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { DynamicSeoHead } from '../components/DynamicSeoHead';
import { supabase } from '../lib/supabase';

interface LandingPageProps {
  onGetStarted: () => void;
  onNavigate: (path: string) => void;
}

export function LandingPage({ onGetStarted, onNavigate }: LandingPageProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLdSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Trezoone",
      "description": "Outil de lucidité financière pour dirigeants de TPE, PME et associations",
      "url": "https://trezoone.fr"
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Trezoone",
      "url": "https://trezoone.fr"
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Trezoone",
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR",
        "priceValidUntil": "2026-12-31"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "127",
        "bestRating": "5",
        "worstRating": "1"
      },
      "description": "Logiciel de gestion de trésorerie et pilotage financier pour TPE, PME et associations. Import de relevés bancaires, rapports financiers automatisés, prévisions de trésorerie et indicateurs de performance.",
      "featureList": [
        "Import de relevés bancaires CSV/Excel/PDF",
        "Catégorisation intelligente des transactions",
        "Rapports financiers mensuels automatisés",
        "Tableaux de bord de trésorerie en temps réel",
        "Prévisions et scénarios budgétaires",
        "Score de pilotage financier",
        "Export PDF professionnel",
        "Aucune connexion bancaire obligatoire"
      ]
    }
  ];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    if (!formData.firstName || !formData.lastName || !formData.company || !formData.email || !formData.password) {
      setSubmitError('Veuillez remplir tous les champs obligatoires');
      setIsSubmitting(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setSubmitError('Les mots de passe ne correspondent pas');
      setIsSubmitting(false);
      return;
    }

    if (formData.password.length < 8) {
      setSubmitError('Le mot de passe doit contenir au moins 8 caractères');
      setIsSubmitting(false);
      return;
    }

    const hasUpperCase = /[A-Z]/.test(formData.password);
    const hasLowerCase = /[a-z]/.test(formData.password);
    const hasNumber = /\d/.test(formData.password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      setSubmitError('Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre');
      setIsSubmitting(false);
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            company: formData.company
          }
        }
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          setSubmitError('Cet email est déjà inscrit. Connectez-vous ou utilisez un autre email.');
        } else {
          setSubmitError('Une erreur est survenue. Veuillez réessayer.');
        }
        return;
      }

      if (authData.user) {
        const trialEndsAt = new Date();
        trialEndsAt.setDate(trialEndsAt.getDate() + 14);
        await supabase.from('user_profiles').update({ trial_ends_at: trialEndsAt.toISOString() }).eq('id', authData.user.id);
      }

      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <DynamicSeoHead
        slug="/"
        fallback={{
          title: "Trezoone – Lucidité financière pour dirigeants de TPE et PME",
          description: "Trezoone transforme vos relevés en projections claires. Comprenez votre trésorerie, identifiez les zones de risque et générez des rapports financiers mensuels automatisés. Aucune connexion bancaire obligatoire.",
          keywords: "gestion trésorerie, pilotage financier, TPE, PME, associations, rapport financier automatisé, tableau de bord trésorerie",
          jsonLd: jsonLdSchemas
        }}
      />
      <div className="min-h-screen bg-gray-50">
        <MarketingHeader
          onNavigate={onNavigate}
          onSignIn={onGetStarted}
          onSignUp={() => document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })}
        />

      <main>
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full mb-6">
              <Award className="w-5 h-5" />
              <span className="text-sm font-semibold">Trezoone, pour que votre trésorerie ne vous empêche plus de dormir.</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Pilotage financier simplifié pour dirigeants non-financiers
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Comprenez votre trésorerie, générez des rapports financiers automatisés et pilotez sereinement votre activité sans être expert-comptable.
            </p>
            <p className="text-lg text-gray-500 mb-8">
              Trezoone transforme vos données bancaires en indicateurs financiers clairs : tableau de bord de trésorerie, analyse des flux financiers, prévisions de trésorerie et reporting mensuel automatisé.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              <button
                onClick={() => document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg flex items-center justify-center gap-2"
              >
                Essai gratuit 14 jours <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => onNavigate('/fonctionnalites')}
                className="bg-white text-gray-900 border-2 border-gray-300 px-8 py-4 rounded-lg hover:border-blue-600 hover:text-blue-600 transition-colors font-semibold text-lg"
              >
                Découvrir les fonctionnalités
              </button>
            </div>
            <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span>Sans engagement</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span>Sans carte bancaire</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span>Configuration en 15 min</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow p-8 border-2 border-gray-100 hover:border-blue-200">
              <div className="bg-blue-100 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Comprenez votre situation financière</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Importez vos relevés bancaires et obtenez une vision claire de vos flux financiers. Identifiez vos encaissements, décaissements et comprenez votre situation de trésorerie.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Import de relevés (Excel/PDF)</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Catégorisation intelligente</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow p-8 border-2 border-gray-100 hover:border-green-200">
              <div className="bg-green-100 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Reporting financier automatisé</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Générez des rapports financiers mensuels professionnels en un clic. Analyse détaillée avec indicateurs de performance, comparatifs mensuels et tendances financières.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Export PDF professionnel</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>KPI financiers personnalisés</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow p-8 border-2 border-gray-100 hover:border-purple-200">
              <div className="bg-purple-100 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                <Target className="h-8 w-8 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Indicateurs de pilotage financier</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Évaluez la santé financière de votre structure avec un score de pilotage mensuel. Identifiez les points d'amélioration et suivez votre progression dans le temps.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Score de santé financière</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Alertes et recommandations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi le pilotage financier est devenu indispensable pour les PME
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Les dirigeants doivent prendre des décisions stratégiques rapidement. Sans outils de gestion financière adaptés, vous pilotez à l'aveugle.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Gain de temps considérable
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Catégorisation intelligente et rapports financiers automatisés. Importez vos relevés et laissez Trezoone analyser.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Activity className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Vision claire et actualisée
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Consultez l'état de votre trésorerie à tout moment sur votre tableau de bord financier centralisé.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <LineChart className="h-10 w-10 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Anticipation des difficultés
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Identifiez les tendances négatives avant qu'elles ne deviennent critiques grâce aux alertes préventives.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Sécurité et conformité
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Vos données financières sont sécurisées et vos rapports respectent les standards comptables.
              </p>
            </div>
          </div>

          <div className="mt-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Comprendre sa trésorerie sans être expert-comptable
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  La plupart des dirigeants de TPE et PME ne sont pas des experts financiers. Trezoone traduit vos données bancaires en indicateurs financiers simples et actionnables : encaissements mensuels, décaissements par catégorie, résultat d'exploitation, position de trésorerie prévisionnelle.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Langage clair, pas de jargon comptable complexe</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Visualisations graphiques intuitives et interactives</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Explications détaillées pour chaque indicateur</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Anticiper plutôt que constater les problèmes de trésorerie
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Trezoone vous permet d'anticiper les tensions de trésorerie : comprenez vos flux financiers, identifiez les tendances baissières, simulez différents scénarios budgétaires et prenez des décisions éclairées avant que les difficultés ne deviennent insurmontables.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Prévisions de trésorerie à 3, 6 et 12 mois</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Alertes automatiques sur les seuils critiques</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Analyses comparatives mensuelles et annuelles</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Fonctionnalités complètes de gestion financière pour PME et associations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trezoone regroupe tous les outils dont vous avez besoin pour un pilotage financier efficace et professionnel.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <PieChart className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Suivi de trésorerie simplifié</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Importez vos relevés bancaires en CSV/Excel ou PDF. TrezoOne analyse vos transactions et calcule vos encaissements, décaissements, soldes et résultat d'exploitation mensuel. Aucune connexion bancaire obligatoire.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Import de relevés (Excel/PDF/CSV)</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Compatible avec toutes les banques</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Catégorisation automatique des transactions</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Détection des doublons</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <FileText className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Rapports financiers mensuels automatisés et exportables</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Chaque mois, Trezoone génère automatiquement un rapport financier professionnel complet : analyse de la situation financière globale, score de pilotage, comparatifs mensuels et annuels, top 3 des charges et recettes, recommandations personnalisées. Exportez vos rapports en PDF pour vos conseils d'administration ou banquiers.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Génération automatique chaque mois</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Export PDF professionnel haute qualité</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Analyse comparative et évolution temporelle</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Recommandations et alertes personnalisées</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <DollarSign className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Budgets prévisionnels et simulations de scénarios</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Créez des budgets prévisionnels annuels et mensuels, simulez différents scénarios financiers (optimiste, pessimiste, réaliste), anticipez vos besoins de trésorerie futurs et mesurez les écarts entre vos prévisions et la réalité. Parfait pour préparer vos demandes de financement bancaire.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Création de budgets prévisionnels illimités</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Simulations de scénarios multiples</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Analyse des écarts budgétaires (budget vs réel)</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Prévisions de trésorerie glissantes</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <Calendar className="h-8 w-8 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Indicateurs clés et tableaux de bord personnalisables</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Trezoone ne vous submerge pas avec des centaines de graphiques incompréhensibles. Accédez uniquement aux indicateurs financiers essentiels : position de trésorerie, évolution mensuelle, ratios de performance, score de santé financière. Tout est clair, lisible et actionnable.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>KPI essentiels (trésorerie, résultat, charges)</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>Graphiques d'évolution temporelle clairs</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>Score de pilotage financier mensuel</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>Alertes sur les seuils critiques configurables</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Qui utilise Trezoone pour piloter leur trésorerie
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Des milliers de dirigeants, agriculteurs, associations et entrepreneurs font confiance à Trezoone pour leur gestion financière quotidienne.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-200">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">TPE et PME sans DAF</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Vous dirigez une petite ou moyenne entreprise de 1 à 50 salariés sans directeur financier dédié. Vous n'avez pas le temps ni les compétences pour passer des heures sur vos chiffres. Trezoone vous offre un pilotage financier professionnel automatisé.
              </p>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Cas d'usage typiques :</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>Artisan, commerçant, consultant indépendant</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>Startup en croissance, scale-up</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>PME familiale, entreprise multi-sites</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border-2 border-green-200">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Award className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Associations loi 1901</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Votre association a besoin de transparence financière pour votre conseil d'administration, vos adhérents et vos financeurs. Les rapports Trezoone sont clairs, compréhensibles par des bénévoles non-financiers et respectent les exigences de reporting associatif.
              </p>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Cas d'usage typiques :</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Association sportive, culturelle, humanitaire</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>ONG, fondation, fonds de dotation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Syndicat, groupement d'intérêt collectif</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 border-2 border-purple-200">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Entrepreneurs et porteurs de projet</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Vous créez votre entreprise ou développez un nouveau projet. Vous avez besoin d'outils de pilotage financier professionnels pour convaincre vos investisseurs, banquiers et partenaires. Trezoone vous aide à présenter des prévisions et reportings crédibles.
              </p>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Cas d'usage typiques :</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>Créateur d'entreprise, auto-entrepreneur</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>Startup levée de fonds, recherche financement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>Repreneur d'entreprise, franchise</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-8 border-2 border-amber-200">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Sprout className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Exploitations agricoles et viticoles</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Votre exploitation agricole fait face à des cycles financiers saisonniers, des aléas climatiques et des investissements lourds. Trezoone gère la complexité de votre trésorerie : aides PAC, avances remboursables, stocks de récoltes, charges de mécanisation et prévisions de trésorerie annuelles adaptées aux cycles agricoles.
              </p>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Cas d'usage typiques :</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>Grandes cultures, élevage, maraîchage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>Vignobles, arboriculture, productions spécialisées</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>GAEC, EARL, SCEA, exploitation individuelle</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 text-center">
              Qu'est-ce que la gestion de trésorerie et pourquoi est-elle cruciale pour votre entreprise ?
            </h2>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                La gestion de trésorerie consiste à suivre, analyser et prévoir les flux financiers entrants et sortants de votre entreprise. C'est l'élément vital de toute organisation : une entreprise rentable sur le papier peut faire faillite si elle manque de liquidités au mauvais moment. La trésorerie représente l'argent disponible immédiatement pour payer vos fournisseurs, vos salaires et vos charges courantes.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Les enjeux du pilotage de trésorerie pour les TPE et PME
              </h3>

              <p className="text-gray-700 leading-relaxed mb-6">
                Pour les dirigeants de TPE et PME, la gestion de trésorerie est souvent perçue comme complexe et chronophage. Pourtant, c'est un levier stratégique majeur. Un pilotage financier efficace permet d'anticiper les difficultés, de saisir les opportunités de croissance et de prendre des décisions éclairées. Les principales difficultés rencontrées par les dirigeants non-financiers incluent : le manque de visibilité sur la situation de trésorerie en temps réel, la difficulté à interpréter les données comptables, l'absence d'outils de prévision adaptés aux petites structures, et le temps considérable nécessaire pour produire des rapports financiers exploitables.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Comment Trezoone simplifie le suivi de trésorerie au quotidien
              </h3>

              <p className="text-gray-700 leading-relaxed mb-6">
                Trezoone transforme la gestion de trésorerie en une activité simple et accessible. Notre logiciel permet d'importer vos relevés bancaires en quelques clics, sans nécessiter de connexion bancaire automatique. La catégorisation intelligente classe automatiquement vos transactions en encaissements et décaissements, en distinguant les flux d'exploitation, d'investissement et de financement. Vous obtenez instantanément une vision claire de votre situation financière, avec des indicateurs clés comme votre solde de trésorerie, votre résultat d'exploitation mensuel, vos principaux postes de dépenses et vos sources de revenus principales.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Prévisions de trésorerie et anticipation des besoins de financement
              </h3>

              <p className="text-gray-700 leading-relaxed mb-6">
                Au-delà du suivi historique, Trezoone vous aide à anticiper vos besoins futurs. Grâce aux outils de prévision de trésorerie intégrés, vous pouvez projeter votre situation financière à 3, 6 ou 12 mois. Ces prévisions prennent en compte vos données historiques, vos budgets prévisionnels et vos scénarios de développement. Cette vision prospective est essentielle pour identifier à l'avance les périodes de tension de trésorerie, planifier vos investissements en fonction de vos capacités financières réelles, préparer vos négociations bancaires avec des éléments concrets, et adapter votre stratégie commerciale en fonction de vos contraintes de trésorerie.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Reporting financier automatisé : gagner du temps et de la crédibilité
              </h3>

              <p className="text-gray-700 leading-relaxed mb-6">
                Produire des rapports financiers de qualité est chronophage et nécessite des compétences comptables approfondies. Trezoone génère automatiquement chaque mois un rapport financier professionnel complet, exportable en PDF. Ces rapports incluent une synthèse de votre situation financière mensuelle, des graphiques d'évolution de vos indicateurs clés, une analyse comparative mensuelle et annuelle, un score de pilotage évaluant la qualité de votre gestion, et des recommandations personnalisées pour améliorer votre performance financière. Ces documents sont parfaits pour communiquer avec vos partenaires : conseil d'administration, banquiers, investisseurs, experts-comptables, ou financeurs institutionnels.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sécurité des données et respect de votre autonomie
              </h3>

              <p className="text-gray-700 leading-relaxed mb-6">
                Contrairement à de nombreux logiciels de gestion bancaire qui imposent une connexion automatique à vos comptes, Trezoone respecte votre autonomie et votre vie privée. Vous importez vos relevés bancaires quand vous le souhaitez, dans le format qui vous convient (CSV, Excel ou PDF). Vos données financières restent sous votre contrôle total. Nous ne revendons aucune donnée, nous n'accédons pas à vos comptes bancaires, et nous ne pratiquons aucun tracking publicitaire. Vos informations sont hébergées en France, sur des serveurs sécurisés conformes aux normes européennes de protection des données RGPD.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Démarrez votre pilotage financier en moins de 15 minutes
              </h3>

              <p className="text-gray-700 leading-relaxed mb-6">
                La mise en place de Trezoone est rapide et intuitive. Créez votre compte en 2 minutes, sans carte bancaire. Importez votre premier relevé bancaire et laissez Trezoone analyser vos transactions. Configurez vos catégories personnalisées si nécessaire, ou utilisez notre catégorisation par défaut adaptée aux TPE et associations. En moins de 15 minutes, vous disposez d'un tableau de bord complet avec tous vos indicateurs financiers à jour. Profitez de 14 jours d'essai gratuit pour tester toutes les fonctionnalités sans engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Trezoone en quelques chiffres
            </h2>
            <p className="text-xl text-gray-600">
              La solution de gestion de trésorerie qui simplifie vraiment le pilotage financier
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center border-t-4 border-blue-600">
              <div className="text-4xl font-bold text-blue-600 mb-2">15 min</div>
              <p className="text-gray-600 font-medium">Configuration initiale</p>
              <p className="text-sm text-gray-500 mt-2">Démarrez votre pilotage financier rapidement</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center border-t-4 border-green-600">
              <div className="text-4xl font-bold text-green-600 mb-2">100%</div>
              <p className="text-gray-600 font-medium">Automatisation</p>
              <p className="text-sm text-gray-500 mt-2">Rapports et catégorisation automatiques</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center border-t-4 border-purple-600">
              <div className="text-4xl font-bold text-purple-600 mb-2">0</div>
              <p className="text-gray-600 font-medium">Connaissance comptable requise</p>
              <p className="text-sm text-gray-500 mt-2">Interface intuitive pour non-financiers</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center border-t-4 border-orange-600">
              <div className="text-4xl font-bold text-orange-600 mb-2">24/7</div>
              <p className="text-gray-600 font-medium">Accès à vos données</p>
              <p className="text-sm text-gray-500 mt-2">Consultez votre trésorerie à tout moment</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Ce que Trezoone vous apporte concrètement</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Respect total de votre autonomie</p>
                      <p className="text-sm text-gray-600">Aucune connexion bancaire obligatoire. Importez vos relevés Excel ou PDF en toute simplicité.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Rapports financiers professionnels automatisés</p>
                      <p className="text-sm text-gray-600">Génération automatique chaque mois avec export PDF pour vos partenaires.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Score de pilotage financier mensuel</p>
                      <p className="text-sm text-gray-600">Évaluez la qualité de votre gestion et identifiez les axes d'amélioration.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Vision claire sans être expert-comptable</p>
                      <p className="text-sm text-gray-600">Interface intuitive, langage simple, indicateurs actionnables.</p>
                    </div>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Notre différence : Votre autonomie avant tout</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Aucune connexion bancaire obligatoire</p>
                      <p className="text-sm text-gray-600">Importez simplement vos relevés Excel ou PDF. Vous gardez le contrôle total de vos données.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Zéro espionnage, zéro revente de données</p>
                      <p className="text-sm text-gray-600">Aucun accès à vos comptes bancaires. Aucune revente de données. Aucun tracking publicitaire.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Catégorisation intelligente</p>
                      <p className="text-sm text-gray-600">Analyse automatique de vos transactions et suggestions pertinentes pour gagner du temps.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Support et accompagnement personnalisé</p>
                      <p className="text-sm text-gray-600">Notre équipe vous aide à optimiser votre pilotage financier.</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="signup" className="py-20 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Commencez gratuitement
            </h2>
            <p className="text-xl text-blue-100">
              Essai gratuit 14 jours — sans carte bancaire, sans engagement
            </p>
          </div>

          {submitSuccess ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Compte créé !</h3>
              <p className="text-gray-600 mb-6">
                Votre compte a été créé avec succès. Vous pouvez maintenant vous connecter et démarrer votre essai gratuit de 14 jours.
              </p>
              <button onClick={onGetStarted} className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Accéder à mon espace
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8">
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Jean"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Dupont"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Entreprise / Association *</label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom de votre structure"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe *</label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                  <p className="mt-1 text-xs text-gray-500">8 caractères min., une majuscule, une minuscule, un chiffre</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirmer le mot de passe *</label>
                  <input
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 text-sm">{submitError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isSubmitting ? 'Création du compte...' : 'Créer mon compte gratuit'}
                </button>

                <p className="text-center text-sm text-gray-500">
                  Déjà un compte ?{' '}
                  <button type="button" onClick={onGetStarted} className="text-blue-600 hover:underline font-medium">
                    Se connecter
                  </button>
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
      </main>

        <MarketingFooter />
      </div>
    </>
  );
}
